import { t } from "i18next";
import { Ref, forwardRef, useImperativeHandle, useState } from "react";
import { useMessage } from "@/contexts";
import { ErrorHelper } from "@/helpers";
import { type RentEnergyFormData, TransferService } from "@/domains/transfer";
// components
import { Button, Form, Input, Modal } from "antd";

export interface RentEnergyModalRef {
  open: (source_address: string) => void;
}

export const RentEnergyModal = forwardRef((_, ref: Ref<RentEnergyModalRef>) => {
  const { messageApi } = useMessage();

  const [showing, setShowing] = useState(false);

  const [form] = Form.useForm<RentEnergyFormData>();

  const handleConfirm = async () => {
    const formValue = await form.validateFields();

    try {
      await TransferService.rentEnergy({
        amount: formValue.amount,
        source: { address: formValue.source_address },
        target: { address: formValue.target_address },
      }).run();
      setShowing(false);
      void messageApi.success(t("systems.Modal.energyRentCreated"));
    } catch (e) {
      if (ErrorHelper.isAxiosError(e)) {
        void ErrorHelper.handleAxiosErrorMessage(e, messageApi);
      } else {
        console.log(e);
      }
    }
  };

  const handleCancel = () => {
    setShowing(false);
  };

  useImperativeHandle(
    ref,
    () => {
      const open = (source_address: string) => {
        form.resetFields();

        form.setFieldsValue({
          source_address,
          target_address: "",
          amount: "",
        });

        setShowing(true);
      };

      return { open };
    },
    [form]
  );

  return (
    <Modal
      title={t("systems.Modal.rentEnergy")}
      open={showing}
      footer={[
        <div className="flex jc:end gap:8" key="footer">
          <Button key="back" onClick={handleCancel}>
            {t("systems.Action.cancel")}
          </Button>
          <Button key="submit" type="primary" onClick={handleConfirm}>
            {t("systems.Action.submit")}
          </Button>
        </div>,
      ]}
      onCancel={handleCancel}
    >
      <Form className="mt:16" form={form} labelCol={{ span: 6 }}>
        {/* formData 包含 source_address */}
        <Form.Item hidden name="source_address">
          <Input type="hidden" />
        </Form.Item>

        <Form.Item
          label={t("domains.Wallet.magicWallet")}
          name="target_address"
          rules={[
            {
              required: true,
              message: t("systems.Error.cannotBeEmpty", {
                field: t("domains.Wallet.magicWallet"),
              }),
            },
          ]}
        >
          <Input placeholder={t("domains.Wallet.magicWalletPlaceholder")} />
        </Form.Item>

        <Form.Item
          label={t("domains.Wallet.rentalAmount")}
          name="amount"
          rules={[
            {
              required: true,
              message: t("systems.Error.cannotBeEmpty", {
                field: t("domains.Wallet.rentalAmount"),
              }),
            },
          ]}
        >
          <Input placeholder={t("domains.Wallet.rentalAmountPlaceholder")} />
        </Form.Item>
      </Form>
    </Modal>
  );
});
